import type { SignupMetadata, NewsletterPreference } from './marketing'
import api, { publicApi } from './api'

export interface LoginCredentials {
  email: string
  password: string
  turnstile_token: string
}

export interface RegisterCredentials extends SignupMetadata {
  name: string
  email: string
  password: string
  password_confirmation: string
  invitation_token?: string
  turnstile_token: string
}

export interface AuthResponse {
  data: {
    user: Partial<NewsletterPreference> & {
      id: string
      name: string
      email: string
      avatar?: string | null
      email_verified_at?: string
      created_at: string
      updated_at: string
    }
    token: string
    token_type: string
  }
}

export interface User extends Partial<NewsletterPreference> {
  id: string
  name: string
  email: string
  avatar?: string | null
  email_verified_at?: string
  created_at: string
  updated_at: string
}

export interface VerifyAccountResponse {
  success: string | boolean,
  message: string,
  error?: string
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const response = await api.post('/auth/register', credentials)
    return response.data
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout')
  },

  async deleteAccount(password?: string): Promise<void> {
    await api.delete('/auth/account', { data: password ? { password } : {} })
  },

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/forgot-password', { email })
    return response.data
  },

  async resetPassword(payload: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }): Promise<{ success: boolean; message: string; error?: string }> {
    const response = await api.post('/auth/reset-password', payload)
    return response.data
  },

  async resendVerification(): Promise<{ success: boolean; message?: string }> {
    const response = await api.post('/auth/resend-verification')
    return response.data
  },

  async getProfile(): Promise<User> {
    const response = await api.get('/auth/profile')
    return response.data?.data?.user ?? response.data
  },

  async refreshToken(): Promise<AuthResponse> {
    const response = await api.post('/auth/refresh')
    return response.data
  },

  async verifyAccount(token: string, email: string): Promise<VerifyAccountResponse> {
    try {
      const response = await api.post('/auth/verify-email', { token, email })
      return response.data
    } catch (error: any) {
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || 'Verification failed',
          error: error.response.data.error || 'Unknown error'
        }
      } else {
        return {
          success: false,
          message: 'Verification failed',
          error: 'Unknown error'
        }
      }
    }
  },
  async exchangeLogin(): Promise<{ data: { user: User & { roles?: string[] }, token: string, is_new_user: boolean, impersonation: boolean } }> {
    const response = await publicApi.post('/auth/exchange', {}, { withCredentials: true })
    return response.data
  },

  // Google OAuth
  async getGoogleAuthUrl(metadata: SignupMetadata = {}): Promise<string> {
    console.log('Making API call to get Google OAuth URL...')
    try {
      const response = await api.get('/auth/google', { withCredentials: true, params: { ...metadata, ...(metadata.newsletter_subscribed === undefined ? {} : { newsletter_subscribed: metadata.newsletter_subscribed ? 1 : 0 }) } })
      console.log('API response:', response)
      console.log('Response data:', response.data)
      const oauthUrl = response.data.data.url
      console.log('Extracted OAuth URL:', oauthUrl)
      return oauthUrl
    } catch (error: any) {
      console.error('API call failed:', error)
      throw new Error(error.response?.data?.message || 'Failed to get Google OAuth URL')
    }
  },

  // Helper functions for local storage
  setToken(token: string): void {
    localStorage.setItem('auth_token', token)
  },

  getToken(): string | null {
    return localStorage.getItem('auth_token')
  },

  setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user))
  },

  getUser(): User | null {
    const user = localStorage.getItem('user')
    return user ? JSON.parse(user) : null
  },

  clearAuth(): void {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
  },

  isAuthenticated(): boolean {
    return !!this.getToken()
  }
}
